/**
 * ProcessingStatus Component
 *
 * Shows real-time document processing status with WebSocket updates.
 * Displays progress through 3 extraction layers with detailed status.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  Circle,
  Loader2,
  AlertCircle,
  FileText,
  ScanText,
  Eye,
  CheckCheck,
  Wifi,
  WifiOff,
  RefreshCw,
  DollarSign,
} from 'lucide-react';
import { useDocumentProcessing, ProcessingProgress } from '../../hooks';
import type { DocumentProcessingStatus } from '../../types';
import * as mhrApi from '../../lib/api';

interface ProcessingStatusProps {
  documentId: string;
  onComplete?: (status: DocumentProcessingStatus) => void;
  useWebSocket?: boolean; // Whether to use WebSocket (default: true)
}

type ProcessingStage = 'pending' | 'extracting' | 'extracted' | 'ocr_pending' | 'ocr_in_progress' | 'vision_verification' | 'completed' | 'failed';

const STAGES: Array<{
  key: ProcessingStage;
  label: string;
  icon: typeof FileText;
  layer?: number;
}> = [
  { key: 'pending', label: 'Uploaded', icon: FileText },
  { key: 'extracting', label: 'Extracting Text (Layer 1)', icon: ScanText, layer: 1 },
  { key: 'ocr_in_progress', label: 'OCR Processing (Layer 2)', icon: ScanText, layer: 2 },
  { key: 'vision_verification', label: 'Vision Verification (Layer 3)', icon: Eye, layer: 3 },
  { key: 'completed', label: 'Completed', icon: CheckCheck },
];

function getStageIndex(status: ProcessingStage): number {
  switch (status) {
    case 'pending':
      return 0;
    case 'extracting':
      return 1;
    case 'extracted':
    case 'ocr_pending':
      return 1; // Still in layer 1 stage area
    case 'ocr_in_progress':
      return 2;
    case 'vision_verification':
      return 3;
    case 'completed':
      return 4;
    case 'failed':
      return -1;
    default:
      return 0;
  }
}

function StageIcon({
  stage,
  currentStageIndex,
  stageIndex,
  failed,
  layerProgress,
}: {
  stage: typeof STAGES[number];
  currentStageIndex: number;
  stageIndex: number;
  failed: boolean;
  layerProgress?: ProcessingProgress['layerProgress'];
}) {
  const Icon = stage.icon;

  // Check layer-specific status
  if (layerProgress && stage.layer) {
    const layerKey = `layer${stage.layer}` as keyof typeof layerProgress;
    const layerStatus = layerProgress[layerKey];

    if (layerStatus.status === 'processing') {
      return <Loader2 size={20} className="text-primary-600 dark:text-primary-400 animate-spin" />;
    }
    if (layerStatus.status === 'complete') {
      return <CheckCircle size={20} className="text-green-500 dark:text-green-400" />;
    }
  }

  // Failed state
  if (failed && stageIndex >= currentStageIndex) {
    return <AlertCircle size={20} className="text-red-500 dark:text-red-400" />;
  }

  // Completed stage
  if (stageIndex < currentStageIndex) {
    return <CheckCircle size={20} className="text-green-500 dark:text-green-400" />;
  }

  // Current stage (in progress)
  if (stageIndex === currentStageIndex) {
    return <Loader2 size={20} className="text-primary-600 dark:text-primary-400 animate-spin" />;
  }

  // Future stage
  return <Circle size={20} className="text-gray-300 dark:text-gray-600" />;
}

export function ProcessingStatus({ documentId, onComplete, useWebSocket = true }: ProcessingStatusProps) {
  const [fallbackStatus, setFallbackStatus] = useState<DocumentProcessingStatus | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);

  // WebSocket connection for real-time updates
  const {
    progress: wsProgress,
    isConnected,
    isConnecting,
    error: wsError,
    connect: reconnect,
  } = useDocumentProcessing(useWebSocket ? documentId : null, {
    onComplete: (result) => {
      if (result.type === 'complete') {
        onComplete?.({
          id: documentId,
          status: 'completed',
          page_count: result.totalPages,
          pages_processed: result.pagesProcessed,
        });
      }
    },
  });

  // Fallback polling when WebSocket is not available or disabled
  const pollStatus = useCallback(async () => {
    try {
      const result = await mhrApi.getDocumentStatus(documentId);
      setFallbackStatus(result);
      setPollingError(null);

      if (result.status === 'completed') {
        onComplete?.(result);
        return true;
      }
      if (result.status === 'failed') {
        setPollingError(result.error || 'Processing failed');
        return true;
      }

      return false;
    } catch (err: any) {
      console.error('[ProcessingStatus] Polling error:', err);
      setPollingError(err.response?.data?.error || 'Failed to fetch status');
      return true;
    }
  }, [documentId, onComplete]);

  // Use polling as fallback when WebSocket is not connected
  useEffect(() => {
    if (useWebSocket && isConnected) {
      // WebSocket is connected, no need for polling
      return;
    }

    let mounted = true;
    let pollInterval: NodeJS.Timeout | null = null;

    const startPolling = async () => {
      const shouldStop = await pollStatus();

      if (!shouldStop && mounted) {
        pollInterval = setInterval(async () => {
          if (mounted) {
            const stop = await pollStatus();
            if (stop && pollInterval) {
              clearInterval(pollInterval);
            }
          }
        }, 3000);
      }
    };

    startPolling();

    return () => {
      mounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [documentId, useWebSocket, isConnected, pollStatus]);

  // Determine which data source to use
  const usingWebSocket = useWebSocket && isConnected && wsProgress;
  const progress = usingWebSocket ? wsProgress : null;
  const status = fallbackStatus;

  // Calculate display values
  const currentStageIndex = progress
    ? getStageIndexFromProgress(progress)
    : status
      ? getStageIndex(status.status as ProcessingStage)
      : 0;

  const failed = progress?.type === 'error' || status?.status === 'failed';
  const completed = progress?.type === 'complete' || status?.status === 'completed';

  const progressPercent = progress
    ? progress.progress
    : (status && status.page_count > 0)
      ? Math.round((status.pages_processed / status.page_count) * 100)
      : 0;

  const pagesProcessed = progress?.pagesProcessed ?? status?.pages_processed ?? 0;
  const totalPages = progress?.totalPages ?? status?.page_count ?? 0;
  const error = wsError || pollingError || progress?.error || status?.error;

  // Format estimated time remaining
  const formatTimeRemaining = (seconds?: number) => {
    if (!seconds || seconds <= 0) return null;
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const timeRemaining = formatTimeRemaining(progress?.estimatedTimeRemaining);

  // Loading state
  if (!progress && !status && !error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <Loader2 size={24} className="text-primary-600 dark:text-primary-400 animate-spin" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header with connection status */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Processing Status</span>
            {useWebSocket && (
              <span
                className={`inline-flex items-center gap-1 text-xs ${
                  isConnected ? 'text-green-600 dark:text-green-400' : isConnecting ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400 dark:text-gray-500'
                }`}
                title={isConnected ? 'Real-time updates active' : isConnecting ? 'Connecting...' : 'Using polling'}
              >
                {isConnected ? <Wifi size={12} /> : isConnecting ? <Loader2 size={12} className="animate-spin" /> : <WifiOff size={12} />}
                {isConnected ? 'Live' : isConnecting ? 'Connecting' : 'Polling'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {totalPages > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {pagesProcessed} / {totalPages} pages
              </span>
            )}
            {!isConnected && useWebSocket && (
              <button
                onClick={reconnect}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-1"
              >
                <RefreshCw size={12} />
                Reconnect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main progress bar */}
      {!failed && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Overall Progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                completed ? 'bg-green-500' : 'bg-primary-600'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>{progress?.message || 'Processing...'}</span>
            {timeRemaining && !completed && (
              <span>Est. {timeRemaining} remaining</span>
            )}
          </div>
        </div>
      )}

      {/* Layer progress bars (WebSocket only) */}
      {progress?.layerProgress && !failed && !completed && (
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 space-y-2">
          {[1, 2, 3].map((layer) => {
            const layerKey = `layer${layer}` as keyof typeof progress.layerProgress;
            const layerStatus = progress.layerProgress[layerKey];
            const layerNames = ['Text Extraction', 'OCR', 'Vision Verification'];

            return (
              <div key={layer} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 w-28">
                  Layer {layer}: {layerNames[layer - 1]}
                </span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      layerStatus.status === 'complete' ? 'bg-green-500' :
                      layerStatus.status === 'processing' ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    style={{ width: `${layerStatus.progress}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500 w-12 text-right">
                  {layerStatus.status === 'complete' ? (
                    <CheckCircle size={12} className="inline text-green-500 dark:text-green-400" />
                  ) : layerStatus.status === 'processing' ? (
                    `${layerStatus.progress}%`
                  ) : (
                    'Pending'
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Stages */}
      <div className="p-4">
        <div className="space-y-3">
          {STAGES.map((stage, idx) => {
            const isActive = idx === currentStageIndex && !failed && !completed;
            const isCompleted = idx < currentStageIndex || (completed && idx <= 4);
            const isFailed = failed && idx >= currentStageIndex;

            return (
              <div
                key={stage.key}
                className={`flex items-center gap-3 ${
                  isActive ? 'text-primary-600 dark:text-primary-400' : isCompleted ? 'text-green-600 dark:text-green-400' : isFailed ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <StageIcon
                  stage={stage}
                  currentStageIndex={currentStageIndex}
                  stageIndex={idx}
                  failed={failed}
                  layerProgress={progress?.layerProgress}
                />
                <span
                  className={`text-sm ${
                    isActive ? 'font-medium' : isCompleted ? 'font-normal' : ''
                  }`}
                >
                  {stage.label}
                </span>
                {isActive && (
                  <span className="text-xs text-primary-500 dark:text-primary-400 ml-auto">In progress...</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Cost display (WebSocket only) */}
        {progress?.costs && (progress.costs.total > 0 || completed) && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <DollarSign size={16} />
              Processing Costs
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div className="flex justify-between">
                <span>OCR:</span>
                <span>${progress.costs.ocr.toFixed(4)}</span>
              </div>
              <div className="flex justify-between">
                <span>Vision:</span>
                <span>${progress.costs.vision.toFixed(4)}</span>
              </div>
              <div className="col-span-2 flex justify-between border-t border-gray-200 dark:border-gray-700 pt-1 font-medium">
                <span>Total:</span>
                <span>${progress.costs.total.toFixed(4)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {failed && error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center text-red-700 dark:text-red-300">
              <AlertCircle size={18} className="mr-2 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Completion message */}
        {completed && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <div className="flex items-center text-green-700 dark:text-green-300">
              <CheckCircle size={18} className="mr-2" />
              <span className="text-sm font-medium">Document processed successfully</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Get stage index from WebSocket progress data
 */
function getStageIndexFromProgress(progress: ProcessingProgress): number {
  if (progress.type === 'complete') return 4;
  if (progress.type === 'error') return -1;

  const { layerProgress, currentLayer } = progress;

  // Layer 1 processing
  if (currentLayer === 1) return 1;

  // Layer 2 processing
  if (currentLayer === 2) return 2;

  // Layer 3 processing
  if (currentLayer === 3) return 3;

  // Determine from layer statuses
  if (layerProgress.layer3.status === 'processing') return 3;
  if (layerProgress.layer2.status === 'processing') return 2;
  if (layerProgress.layer1.status === 'processing') return 1;

  return 0;
}

export default ProcessingStatus;
